// WalletsContext — ONE wallet list for the whole app, and the single answer to
// "which wallet am I acting as right now".
//
// The trading recipient is the account's primary wallet (`is_primary`) when
// one is set. Header / Spot / the wallet popover sum `is_selected` wallets
// instead — selecting two blue squares shows both balances. Primary is a
// badge, not a switcher.
//
// Mounting this once at the root also means one /v1/me/wallets fetch per app
// load rather than one per consumer. When the userland gate is off the fetch
// fails, status is 'offline', and consumers render their honest empty state.

import { type ReactNode, createContext, useContext, useMemo } from 'react'
import type { UserlandWallet } from '../api/userland'
import { type UseUserlandWallets, useUserlandWallets } from '../hooks/useUserlandWallets'

export type WalletsContextValue = UseUserlandWallets & {
  /** The account's primary wallet, or null when there is none (or gate off). */
  active: UserlandWallet | null
  /** Live wallets included in the product context (blue squares). */
  selected: UserlandWallet[]
  /** The active primary wallet used as the trade-plan recipient. */
  tradeRecipient: UserlandWallet | null
  /** Promote a wallet to primary. */
  setActive: (id: string) => Promise<void>
}

const Ctx = createContext<WalletsContextValue | null>(null)

export function WalletsProvider({ children }: { children: ReactNode }) {
  const wallets = useUserlandWallets()

  const value = useMemo<WalletsContextValue>(() => {
    // An archived wallet can still hold the primary flag; it must not act as
    // the app's active wallet, so fall back to the first live one.
    const live = wallets.wallets.filter((w) => !w.archived)
    const primary = live.find((w) => w.is_primary) ?? null
    const selected = live.filter((w) => w.is_selected)
    // Never invent a trading identity: the backend authorizes only the active
    // primary wallet. First create/import already marks that wallet primary.
    const active = primary
    return {
      ...wallets,
      active,
      selected,
      tradeRecipient: active,
      setActive: wallets.makePrimary,
    }
  }, [wallets])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useWallets(): WalletsContextValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useWallets must be used inside <WalletsProvider>')
  return ctx
}

export function useOptionalWallets(): WalletsContextValue | null {
  return useContext(Ctx)
}

/** The wallet every wallet-scoped surface acts as. Null when none is available. */
export function useActiveWallet(): UserlandWallet | null {
  return useWallets().active
}

export function useTradeRecipient(): UserlandWallet | null {
  return useWallets().tradeRecipient
}

/** Where the wallet list is: 'loading' until its first answer. A surface that
 *  says "no wallet" must ask this first — an empty list that has not answered
 *  yet is not an account without a wallet. */
export function useWalletsStatus(): WalletsContextValue['status'] {
  return useWallets().status
}
